import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

// Button, Link, and a single layout host of those CTAs. A BoundComponent
// (or any other wrapper) with several children is not unwrapped — authors
// should not nest layout in `trigger` except as that one host.
const CTA_DISPLAY_NAMES = new Set([
  'Button',
  'Link',
  'a',
  'button',
  'ModalTrigger',
]);

function getElementDisplayName(type: React.ReactElement['type']): string {
  if (typeof type === 'string') {
    return type;
  }
  if (typeof type === 'function') {
    const fn = type as { displayName?: string; name?: string };
    return fn.displayName || fn.name || '';
  }
  if (typeof type === 'object' && type !== null) {
    const maybe = type as {
      displayName?: string;
      render?: { displayName?: string };
    };
    return maybe.displayName || maybe.render?.displayName || '';
  }
  return '';
}

function isCtaElement(element: React.ReactElement): boolean {
  return CTA_DISPLAY_NAMES.has(getElementDisplayName(element.type));
}

/** Flattens a trigger slot into its CTAs, looking through Fragments and one layout host. */
export function collectTriggerChildren(
  node: React.ReactNode,
): React.ReactNode[] {
  return React.Children.toArray(node).flatMap((child) => {
    if (child == null || typeof child === 'boolean') {
      return [];
    }
    if (!React.isValidElement(child)) {
      return [child];
    }
    if (child.type === React.Fragment) {
      return collectTriggerChildren(
        (child.props as { children?: React.ReactNode }).children,
      );
    }
    const nested = (child.props as { children?: React.ReactNode }).children;
    const nestedList = React.Children.toArray(nested).filter(
      (item) => item != null && typeof item !== 'boolean',
    );
    if (nestedList.length > 1 && !isCtaElement(child)) {
      return nestedList.flatMap((item) => collectTriggerChildren(item));
    }
    return [child];
  });
}

/**
 * Radix keeps `aria-controls` on the trigger even while the overlay is closed
 * and unmounted. Drop it until open so scanners do not flag a missing ID.
 */
export const TriggerAriaBridge = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { asChild?: boolean }
>(function TriggerAriaBridge(
  {
    asChild = false,
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'span';
  const isExpanded = ariaExpanded === true || ariaExpanded === 'true';
  return (
    <Comp
      ref={ref}
      aria-expanded={ariaExpanded}
      aria-controls={isExpanded ? ariaControls : undefined}
      {...props}
    />
  );
});
